import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowUpRight, CheckCircle2, XCircle } from "lucide-react";
import type { JobResultItem } from "@repo/shared";
import {
  Badge,
  DataTable,
  StatusBadge,
  localTablePage,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";

import { useTableDensity } from "@/lib/use-table-density";
import { paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";

type ResultRow = { id: string; item: JobResultItem };

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** The items of a finished job, failures first. Each group is its own table, searchable and paged on the client. */
export function ResultsTables({ items }: { items: JobResultItem[] }) {
  const { t } = useTranslation("jobs");

  const { failed, succeeded } = useMemo(() => {
    const rows = items.map((item, index) => ({ id: String(index), item }));
    return {
      failed: rows.filter((row) => !row.item.success),
      succeeded: rows.filter((row) => row.item.success),
    };
  }, [items]);

  return (
    <div className="flex flex-col gap-6">
      {failed.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-status-danger" strokeWidth={1.8} aria-hidden />
            <h2 className="text-sm font-semibold text-ink">
              {t("detail.results.failedItems", { count: failed.length })}
            </h2>
          </div>
          <ResultsTable
            label={t("detail.results.failedItems", { count: failed.length })}
            rows={failed}
            outcome="failed"
          />
        </section>
      )}

      {succeeded.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-status-success" strokeWidth={1.8} aria-hidden />
            <h2 className="text-sm font-semibold text-ink">
              {t("detail.results.succeededItems", { count: succeeded.length })}
            </h2>
          </div>
          <ResultsTable
            label={t("detail.results.succeededItems", { count: succeeded.length })}
            rows={succeeded}
            outcome="succeeded"
          />
        </section>
      )}
    </div>
  );
}

function ResultsTable({
  label,
  rows,
  outcome,
}: {
  label: string;
  rows: ResultRow[];
  outcome: "failed" | "succeeded";
}) {
  const { t } = useTranslation("jobs");
  const { density } = useTableDensity();
  const [state, setState] = useState<DataTableState>({
    page: 1,
    pageSize: PAGE_SIZE_OPTIONS[0],
    query: "",
    filters: {},
    selection: [],
  });

  const columns = useMemo<DataTableColumn<ResultRow>[]>(() => {
    const itemName = ({ item }: ResultRow) => {
      if (item.itemType === "variable" || item.variableName) {
        return item.variableName || t("detail.results.unknownVariable");
      }
      return item.itemTitle || t("detail.results.unknownItem");
    };
    const appName = ({ item }: ResultRow) => item.appName || item.appId;

    const itemColumn: DataTableColumn<ResultRow> = {
      id: "item",
      header: t("detail.results.colItem"),
      sort: "text",
      value: itemName,
      wrap: true,
      cell: (row) => (
        <div>
          <span className="font-mono">{itemName(row)}</span>
          {row.item.itemType && (
            <Badge variant="outline" className="ml-2">
              {row.item.itemType}
            </Badge>
          )}
          {outcome === "succeeded" && row.item.warnings && row.item.warnings.length > 0 && (
            <ul className="mt-1.5 flex flex-col gap-1">
              {row.item.warnings.map((warning, index) => (
                <li
                  key={index}
                  className="flex items-start gap-1 font-sans text-xs text-status-warning-foreground"
                >
                  <AlertTriangle
                    className="mt-0.5 h-3 w-3 shrink-0"
                    strokeWidth={1.8}
                    aria-hidden
                  />
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ),
    };

    const appColumn: DataTableColumn<ResultRow> = {
      id: "app",
      header: t("detail.results.colApp"),
      sort: "text",
      value: appName,
      cell: (row) =>
        row.item.appUrl ? (
          <a
            href={row.item.appUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-dim hover:underline"
          >
            {appName(row)}
            <ArrowUpRight className="h-3 w-3" aria-hidden />
          </a>
        ) : (
          <span className="text-dim">{appName(row)}</span>
        ),
    };

    return [
      itemColumn,
      appColumn,
      outcome === "failed"
        ? {
            id: "error",
            header: t("detail.results.colError"),
            value: ({ item }) => item.error,
            wrap: true,
            cell: ({ item }) => (
              <span className="font-mono text-sm text-status-danger-foreground">{item.error}</span>
            ),
          }
        : {
            id: "status",
            header: t("detail.results.colStatus"),
            width: 130,
            cell: () => <StatusBadge tone="success">{t("detail.results.success")}</StatusBadge>,
          },
    ];
  }, [t, outcome]);

  const page = localTablePage(rows, state, { columns });
  // A short list needs neither a search box nor pagination.
  const short = rows.length <= PAGE_SIZE_OPTIONS[0];

  return (
    <DataTable
      label={label}
      columns={columns}
      rows={page.rows}
      getRowId={(row) => row.id}
      state={state}
      onStateChange={setState}
      density={density}
      messages={tableMessages({ empty: { title: t("detail.results.empty") } })}
      pagination={
        short
          ? undefined
          : {
              kind: "offset",
              total: page.total,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              labels: paginationLabels(),
            }
      }
      search={short ? undefined : tableSearch(t("detail.results.searchPlaceholder"))}
    />
  );
}
